import { Link } from "react-router-dom";
import { useMemo } from "react";
import { ArrowUpRight, Download, Shield, Zap, Waves, Wind, Cpu } from "lucide-react";
import { HERO_STATS, APPLICATIONS, BRAND } from "../lib/content";
import SectionHeader from "../components/SectionHeader";
import LogDriveDiagram from "../components/LogDriveDiagram";
import BenchmarkChart from "../components/BenchmarkChart";
import SEO from "../components/SEO";
import { openCapabilitiesDeck } from "../lib/api";

const ICONS = {
  defense: Shield,
  aerospace: Zap,
  marine: Waves,
  energy: Wind,
  robotics: Cpu,
};

const TICKER_WORDS = [
  "Logarithmic Drive",
  "Hollow shaft architecture",
  "225:1 two-stage",
  ">90% single-stage efficiency",
  "Contra-rotating propellers",
  "Partner-ready",
  "Defense · Aerospace · Marine · Energy",
];

export default function HomePage() {
  const tickerItems = useMemo(
    () => Array.from({ length: 2 }).flatMap((_, k) => TICKER_WORDS.map((w, i) => ({ key: `${k}-${i}`, w }))),
    []
  );
  const visibleApps = useMemo(
    () => APPLICATIONS.filter((app) => app.slug !== "defense"),
    []
  );

  return (
    <main data-testid="page-home">
      <SEO path="/" />
      {/* HERO */}
      <section className="relative overflow-hidden bg-scanlines bg-noise" data-testid="hero">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.pexels.com/photos/33917387/pexels-photo-33917387.jpeg"
            alt=""
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/45 to-black" />
        </div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-20 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 relative z-10">
            <div className="reveal font-mono-tech text-[11px] uppercase tracking-[0.3em] text-white/60">
              / partnership brief · classified program-ready
            </div>
            <h1 className="reveal reveal-delay-1 mt-6 font-display font-black text-[12vw] md:text-[7.2vw] leading-[0.88] tracking-tighter">
              THE FIRST
              <br />
              PRACTICAL
              <br />
              <span className="text-white/90 inline-flex items-center gap-4">
                REDUCER
                <span className="hidden md:inline-block h-[0.7em] w-2 bg-[#FF3B30] caret align-middle" />
              </span>
              <br />
              IN <span className="text-[#FF3B30]">50 YEARS</span>.
            </h1>
            <p className="reveal reveal-delay-2 mt-8 max-w-xl text-white/70 text-base md:text-lg leading-relaxed">
              A hollow-shaft, bearing-ball reduction architecture engineered for
              defense rotorcraft, eVTOL propulsion, robotics, contra-rotating
              marine drives and energy generation. Built from first principles.
              Purpose-built for development partners.
            </p>
            <div className="reveal reveal-delay-3 mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-briefing"
                className="inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5C53] text-white font-mono-tech uppercase tracking-widest text-xs px-7 py-4"
              >
                Request partnership briefing
                <ArrowUpRight size={16} />
              </Link>
              <button
                type="button"
                onClick={openCapabilitiesDeck}
                data-testid="hero-cta-capabilities"
                className="inline-flex items-center gap-3 border border-white/30 hover:border-white hover:bg-white/5 text-white font-mono-tech uppercase tracking-widest text-xs px-7 py-4"
              >
                <Download size={14} /> Capabilities brief
              </button>
            </div>
          </div>

          <div className="md:col-span-5 relative z-10">
            <div className="reveal reveal-delay-2 border border-white/15 bg-black/40 backdrop-blur">
              <div className="relative aspect-square w-full diagram-grid">
                <LogDriveDiagram />
                <div className="absolute top-3 left-3 font-mono-tech text-[10px] uppercase tracking-widest text-white/60 bg-black/60 px-2 py-1 border border-white/15">
                  live · motion paths
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ticker */}
        <div className="relative border-y border-white/10 overflow-hidden bg-black">
          <div className="ticker flex whitespace-nowrap py-4 font-mono-tech text-xs uppercase tracking-[0.25em] text-white/50">
            {tickerItems.map(({ key, w }) => (
              <span key={key} className="flex items-center gap-6 pr-10">
                {w}
                <span className="inline-block h-1 w-1 bg-[#FF3B30]" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* BENCHMARK — power & torque density vs the field */}
      <BenchmarkChart />

      {/* HARDWARE — actual drive, two-stage 121:1 */}
      <section className="relative py-24 md:py-32 border-b border-white/5 bg-[#050505]" data-testid="hardware">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="relative border border-white/10 bg-black overflow-hidden">
              <img
                src="/hardware/two-stage-121.png"
                alt="Two stage 121:1 LogDrive — actual hardware"
                className="block w-full h-auto"
              />
              <div className="absolute top-3 left-3 font-mono-tech text-[10px] uppercase tracking-widest text-white/80 bg-black/70 px-2 py-1 border border-white/15">
                / actual hardware
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between font-mono-tech text-[11px] uppercase tracking-widest text-white/60">
              <span>FIG. 01 — Two stage 121:1 drive</span>
              <span className="text-white/40">our shop</span>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-white/50">
              / 02 — hardware
            </div>
            <h2 className="mt-4 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              Two stage.
              <br />
              <span className="text-[#FF3B30]">121:1</span>.
              <br />
              In hand.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              A working two-stage LogDrive built and tested in our shop.
              Coaxial, hollow-shaft, sealed — the same architecture
              partners can scale up for rotorcraft gearboxes, eVTOL rotor
              drives, contra-rotating propellers and humanoid actuation.
            </p>
            <div className="mt-8 border border-white/10">
              <div className="grid grid-cols-12 px-4 py-3 bg-[#0a0a0a] border-b border-white/10 font-mono-tech text-[10px] uppercase tracking-widest">
                <div className="col-span-4 text-white/50">Spec</div>
                <div className="col-span-4 text-white flex items-center gap-2">
                  <span className="h-1.5 w-1.5 bg-[#FF3B30] inline-block" />
                  LogDrive
                </div>
                <div className="col-span-4 text-white/40">
                  Nabtesco RV-40E-121
                </div>
              </div>
              {[
                ["Transmission ratio", "121:1", "121:1"],
                ["Rated output speed", "15 RPM", "15 RPM"],
                ["Rated torque", "565 Nm", "412 Nm", "+37%"],
                ["Service life", "6,000 hr", "6,000 hr"],
                ["Weight", "6.5 kg", "9.3 kg"],
              ].map(([k, l, n, badge]) => (
                <div
                  key={k}
                  className="grid grid-cols-12 px-4 py-3 border-t border-white/5 items-center"
                >
                  <div className="col-span-4 font-mono-tech text-[11px] uppercase tracking-widest text-white/55">
                    {k}
                  </div>
                  <div className="col-span-4 flex items-baseline gap-2">
                    <span className="font-display font-bold text-white text-base md:text-lg">
                      {l}
                    </span>
                    {badge && (
                      <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF3B30] border border-[#FF3B30]/40 px-1.5 py-0.5">
                        {badge}
                      </span>
                    )}
                  </div>
                  <div className="col-span-4 font-mono-tech text-sm text-white/45">
                    {n}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
              * Nabtesco RV-40E-121 figures from manufacturer datasheet · same
              ratio class · LogDrive delivers higher rated torque in a
              comparable envelope.
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IT IS — explanation + wedge principle video */}
      <section className="relative py-24 md:py-32 border-b border-white/5" data-testid="overview">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <SectionHeader
              kicker="/ 03 — system"
              title={<>A new class of<br />speed reducer.</>}
              lead={`${BRAND.short} is based on a fundamental theory of wedges oriented in a circular pattern. Gear teeth are replaced by a simultaneously-loaded bank of bearing balls. This approach has lower contact stresses, higher power density, coaxial, hollow form factor that drops into propulsion architecture that defense primes and eVTOL OEMs are already building.`}
            />
          </div>

          <div className="md:col-span-6">
            <div className="border border-white/10 bg-black overflow-hidden" data-testid="wedge-principle-card">
              <div className="px-4 py-3 flex items-center justify-between border-b border-white/10 bg-[#0a0a0a]">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/60">
                  / wedge principle · captured rig
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 bg-[#FF3B30]" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/60">
                    rec
                  </span>
                </div>
              </div>
              <video
                src="/media/wedge-principle.mp4"
                className="block w-full h-auto bg-black"
                autoPlay
                loop
                muted
                playsInline
                controls
                preload="metadata"
                data-testid="wedge-video-home"
              />
              <div className="px-4 py-3 border-t border-white/10 grid grid-cols-3 gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                <span>α₁ low-angle in</span>
                <span className="text-center">ball · radial</span>
                <span className="text-right">α₂ high-angle out</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STAT STRIP — instrumentation */}
      <section className="relative py-20 bg-[#0a0a0a] border-y border-white/5" data-testid="stats">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
            / instrumentation
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-8">
            {HERO_STATS.map((s) => (
              <div key={s.k} className="border-l border-white/10 pl-5">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                  {s.k}
                </div>
                <div className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTER-ROTATING CLAIM */}
      <section className="relative py-20 md:py-28 border-b border-white/5 bg-noise overflow-hidden" data-testid="counter-rotating-claim">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#FF3B30]">
              / counter-rotating topology
            </div>
            <h2 className="mt-5 font-display font-black text-4xl sm:text-5xl md:text-7xl tracking-tighter leading-[0.92]">
              The <span className="text-[#FF3B30]">highest</span> power-to-weight
              and torque-to-weight actuators and generators in the industry.
            </h2>
            <p className="mt-6 text-white/70 max-w-2xl leading-relaxed">
              By pairing two coaxial, counter-rotating outputs in a single compact
              unit, LogDrive collapses what is traditionally a gearbox + motor +
              reducer stack into one part — unlocking actuator and generator
              envelopes competitors can&apos;t match.
            </p>
          </div>
          <div className="md:col-span-4 grid grid-cols-2 gap-3">
            <div className="border border-white/10 p-5">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                Power / weight
              </div>
              <div className="mt-1 font-display font-black text-3xl tracking-tighter">
                Best-in-class
              </div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                Torque / weight
              </div>
              <div className="mt-1 font-display font-black text-3xl tracking-tighter">
                Best-in-class
              </div>
            </div>
            <div className="border border-white/10 p-5 col-span-2">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                Platforms
              </div>
              <div className="mt-1 text-white/90 leading-snug">
                Humanoids · Exoskeletons · UGV / UAS · Generators · eVTOL lift rotors
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="relative py-24 md:py-32" data-testid="applications-preview">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <SectionHeader
              kicker="/ 02 — deployable"
              title={<>Where it goes<br />to work.</>}
            />
            <Link to="/applications" className="font-mono-tech text-[11px] uppercase tracking-widest text-white/70 hover:text-white u-link">
              All applications →
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleApps.map((app) => {
              const Icon = ICONS[app.slug];
              return (
                <Link
                  key={app.slug}
                  to="/applications"
                  data-testid={`app-card-${app.slug}`}
                  className="group relative overflow-hidden border border-white/10 hover:border-white/30 transition-colors"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={app.image}
                      alt=""
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                    <div className="absolute top-4 left-4 font-mono-tech text-[10px] uppercase tracking-widest text-white/80 bg-black/50 px-2 py-1 border border-white/15">
                      {app.kicker}
                    </div>
                    <div className="absolute bottom-4 right-4">
                      <Icon size={24} className="text-white/80" />
                    </div>
                  </div>
                  <div className="p-6 bg-[#0a0a0a]">
                    <div className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                      {app.title}
                    </div>
                    <p className="mt-3 text-white/60 text-sm leading-relaxed">
                      {app.blurb}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest text-white group-hover:text-[#FF3B30]">
                      Explore sector <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI ASSISTANT TEASER */}
      <section className="relative py-24 md:py-32 border-y border-white/5 bg-[#0a0a0a]" data-testid="assistant-teaser">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50">
              / 03 — partnership assistant
            </div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-5">
              A technical partner — on call.
            </h2>
            <p className="mt-5 text-white/70 max-w-xl leading-relaxed">
              An AI assistant briefed on the Logarithmic Drive answers technical
              questions from prospective partners in real time — from reduction
              ratios and efficiency numbers to target configurations and the
              path from NDA to prototype.
            </p>
            <div className="mt-6 font-mono-tech text-[11px] uppercase tracking-widest text-white/60">
              → Open the assistant (bottom-right) or send a briefing request.
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="border border-white/15 bg-black">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/60">
                  session // partner-0x7F · secure
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 bg-[#FF3B30]" />
                  <span className="h-2 w-2 bg-white/30" />
                  <span className="h-2 w-2 bg-white/30" />
                </div>
              </div>
              <div className="p-5 space-y-4 text-sm">
                <div className="flex">
                  <div className="max-w-[80%] bg-white text-black px-3 py-2 border border-white">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest opacity-60">you</div>
                    What reduction ratio and efficiency do you deliver?
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="max-w-[85%] bg-[#0a0a0a] border border-white/10 text-white/90 px-3 py-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest opacity-60">assistant</div>
                    Single-stage LogDrive delivers 5:1 to 15:1 with theoretical efficiency above 90%.
                    Two-stage configurations reach 225:1 with efficiency above 80%. Hollow-shaft, coaxial —
                    drop-in for actuator, rotor, and propeller drives.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative py-24 md:py-32" data-testid="cta-strip">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50">
              / 04 — engage
            </div>
            <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.9] mt-5">
              Bring a mission.
              <br />
              Leave with a <span className="text-[#FF3B30]">roadmap</span>.
            </h2>
            <p className="mt-6 text-white/70 max-w-xl">
              We're actively scoping partnerships with defense primes, eVTOL OEMs,
              propulsion integrators, and energy companies. Every engagement starts
              with a 30-minute technical briefing.
            </p>
          </div>
          <div className="md:col-span-5 grid gap-3">
            <Link
              to="/contact"
              data-testid="cta-strip-briefing"
              className="flex items-center justify-between border border-white/15 hover:border-white p-5 group"
            >
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                  01
                </div>
                <div className="font-display text-xl font-bold mt-1">
                  Request a partnership briefing
                </div>
              </div>
              <ArrowUpRight className="group-hover:text-[#FF3B30]" />
            </Link>
            <Link
              to="/contact#schedule"
              data-testid="cta-strip-meeting"
              className="flex items-center justify-between border border-white/15 hover:border-white p-5 group"
            >
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                  02
                </div>
                <div className="font-display text-xl font-bold mt-1">
                  Schedule a technical meeting
                </div>
              </div>
              <ArrowUpRight className="group-hover:text-[#FF3B30]" />
            </Link>
            <button
              type="button"
              onClick={openCapabilitiesDeck}
              data-testid="cta-strip-deck"
              className="flex items-center justify-between border border-white/15 hover:border-white p-5 group w-full text-left"
            >
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                  03
                </div>
                <div className="font-display text-xl font-bold mt-1">
                  Download the capabilities brief
                </div>
              </div>
              <Download className="group-hover:text-[#FF3B30]" />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
